import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, Output, EventEmitter } from "@angular/core";
import { Router } from "@angular/router";
import {
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from "@azure/msal-angular";
import {
  AccountInfo,
  AuthenticationResult,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import { environment } from "@env/environment";
import { Observable, Subject } from "rxjs";
import { IUser } from "./auth.models";
import { SharedService } from "@shared/services/shared.service";
// import { UserService } from "app/services/user/user.service";

@Injectable({
  providedIn: "root",
})

export class AuthService {
  public user: IUser = null;
  public static b2cAuthType: string = "B2C";
  public static aadAuthType: string = "AAD";
  userRoles: string[] = [];
  public rolesUpdated: Subject<string[]> = new Subject<string[]>();
  private readonly _destroying$ = new Subject<void>();
  msalBroadcastService: any;
  public rolesChanged$ = this.rolesUpdated.asObservable();

  // public processing: boolean = true;
  public loading: boolean = true;

  @Output() currentUserEvent = new EventEmitter<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private http: HttpClient,
    private sharedService: SharedService,
    private router: Router
    ) {
    // MSAL initialization is now handled in app.component.ts
  }

  isAuthenticated(): boolean {
    let getAllAccounts = this.authService.instance.getAllAccounts();

    let authenticated = getAllAccounts.length > 0;
    if (authenticated) {
      this.setUser(getAllAccounts[0]);
      this.currentUserEvent.emit();
    }

    return authenticated;
  }

  public getUserRoles(): Observable<string[]> {
    return this.http.get<string[]>(environment.privateApi + "User/roles");
  }

  public getUserNameClaim(): Observable<string> {
    return this.http.get<string>(environment.privateApi + "User/user/claim/name", { responseType: 'text' as 'json' });
  }

  // public getUserRolesByEmail(email: string) : Observable<string[]> {
  //   return this.http.get<string[]>(environment.privateApi + 'User/userRoles/' + email,  { withCredentials: true });
  // }  
  
  public getUserRolesByEmail(email: string) : Observable<string[]> {
    return this.http.get<string[]>(environment.privateApi + 'User/roles' ,  { withCredentials: true });
  }

  public testInfo() : Observable<any> {
    return this.http.get<any>(environment.publicApi + 'Application/info',  { withCredentials: true });
  }

  public loadUserInformation(): void {
    this.sharedService.loading = true;
    // console.log("Loading user information...");
    this.getAADUser();
    if (this.user != null) {
      this.getUserRolesByEmail(this.user.email).subscribe({
        next: response => {
          this.userRoles = response;
          if (this.userRoles == null || this.userRoles.length == 0) {
            this.loading = false;
          }
          this.rolesUpdated.next(response);
          this.sharedService.loading = false;
        },
        error: e => {
          console.error("auth error: " + e.statusText);
          this.sharedService.loading = false;
        }
      });
    }
  }

  public isAuthorized(roles?: string[]): boolean {
    let authorized = this.isAuthenticated();
    if (roles != null && roles.length > 0) {
      return (authorized && this.userHasAtLeastOneRole(roles));
    }
    return authorized;
  }

  public userHasAtLeastOneRole(roles: string[]): boolean {
    if (roles == null || roles.length == 0 || this.userRoles.length == 0) {
      return false;
    }

    for (let role of roles) {
      if (this.userRoles.findIndex((r) => r == role) >= 0) {
        return true;
      }
    }
    return false;
  }

  public get username(): string {
    let account = this.authService.instance.getAllAccounts()[0];
    if (account) {
      return this.formatName(account.idTokenClaims["given_name"] as string)
             + " " +
             this.formatName(account.idTokenClaims["family_name"] as string);
    }
    return null;
  }

  public get isAdmin(): boolean {
    return this.userRoles.includes('Admin');
  }

  formatName(name: string): string{
    return name[0].toUpperCase() + name.slice(1).toLowerCase();
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    this.loading = true;
    // console.log("login called", this.msalGuardConfig);

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
            this.loadUserInformation();
            this.loading = false;
          });
      } else {
        this.authService
          .loginPopup(userFlowRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
            this.loadUserInformation();
            this.loading = false;
          });
      }
    } else {

      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
          ...userFlowRequest,
        } as RedirectRequest);

        this.loading = false;
      } else {
        this.authService.loginRedirect(userFlowRequest);
      }
    }
  }

  logout() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: "/",
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  public isProcessing(): boolean {
    return this.sharedService.loading;
  }

  public getUser(): IUser {
    return this.user;
  }

  public getAADUser(): IUser {
    let account = this.authService.instance.getAllAccounts()[0];

    if (account) {
      this.setUser(account);
      return this.user;
    }
    return null;
  }

  public setUser(account: AccountInfo): void {
    if (this.user == null && account != null) {
      this.user = {
        fullName: account.idTokenClaims["given_name"] + " " + account.idTokenClaims["family_name"],
        userName: account.idTokenClaims["preferred_username"],
        active: true,
        email: account.idTokenClaims["preferred_username"],
        mobile: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        manager: '',
        jobTitle: '',
        isAuthenticated: true,
        timestamp: new Date(),
        roles: this.userRoles,
      };
      sessionStorage.setItem("user", this.user.fullName);
    }
  }

}


